import { useEffect, useState, type ReactNode } from 'react'
import { alreadyTried, checkDeployedBuild, decideBoot, reloadGuard, takeOverAndReload, type KeyValue } from '../../lib/appUpdate'
import { newerDataSeen } from '../../lib/readOnly'
import { Splash } from '../AuthGate'

/**
 * QUIL: the stale-build gate (app-platform W14 §3.1, F14).
 *
 * Mounted in App.tsx around <AuthGate>, so it decides before anything signs in or touches
 * OneDrive. At boot it asks /version.json (no-store, 5 s timeout) whether the deployed build is
 * this one. Behind: hold the app on the splash, take the new worker over and reload (once per
 * deployed build per session, so a half-propagated deploy cannot loop). Everything else fails
 * open: offline, a timeout, a malformed file, or a takeover that found nothing to apply all render
 * the app, and the update toast still offers the new build when its worker arrives.
 *
 * Newer data (`newerData`) is the input W12's NewerDataError feeds. Since W12 step A5 (2026-10-09)
 * `quilScope.openStore` raises `NewerDataError` when the local store is ahead of this build and
 * records it (`lib/readOnly.ts`). The session that meets it opens the store read-only (every local
 * write and every OneDrive request is refused, and the sync pill says why); a later boot of the same
 * build lands on the shared gate's read-only branch.
 */
export function BuildGate({
  children,
  enabled = import.meta.env.PROD,
  runningId = __BUILD_INFO__.buildId,
  newerData = newerDataSeen,
}: {
  children: ReactNode
  enabled?: boolean
  runningId?: string
  /** Whether an earlier boot of this build met local data from a newer one (`readOnly.ts`). */
  newerData?: () => boolean
}) {
  const [ready, setReady] = useState(!enabled)

  useEffect(() => {
    if (!enabled) return
    let live = true
    void (async () => {
      const check = await checkDeployedBuild(runningId)
      const storage = session()
      const tried = check.state === 'behind' && alreadyTried(storage, check.deployed.buildId)
      const decision = decideBoot({
        check,
        swSupported: typeof navigator !== 'undefined' && 'serviceWorker' in navigator,
        newerData: newerData(), // W12: an earlier boot of THIS build found the local store ahead of it
        alreadyTried: tried,
      })
      if (decision.action === 'update') {
        reloadGuard(storage, decision.deployed.buildId)
        if ((await takeOverAndReload()) === 'reloading') return // the page is about to go: keep holding
      }
      // run, run-stale, or a takeover that did not take: fail open.
      if (live) setReady(true)
    })()
    return () => { live = false }
  }, [enabled, runningId, newerData])

  return ready ? <>{children}</> : <Splash />
}

function session(): KeyValue | null {
  try {
    return typeof sessionStorage !== 'undefined' ? sessionStorage : null
  } catch {
    return null
  }
}
